import React from "react";
import { Badge, OverlayTrigger, Stack } from "react-bootstrap";
import THEME from "../../../../theme/index";
import PhaseListStyles from "./PhaseList.styles";
import { IPhaseListItemProps } from "./PhaseList.types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import CardUtils from "../../Card.utils";
import { IdNamePairVM } from "../../../../api/generated/models";
import { isMobile } from "react-device-detect";
import { EPhaseStates } from "../../../../shared/enums/phases.enums";
import TOOL_TIP from "../../../tooltip/ToolTip.component";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";

/* istanbul ignore next */
const renderBadge = (
  status: IdNamePairVM | undefined,
  sendDataToGTM: ReturnType<typeof useGTMDispatch>
): React.ReactNode => {
  const badgeColorFlag = CardUtils.getBadgeFormat(status?.name ?? "").colorFlag;
  const badgeLabel = CardUtils.getBadgeFormat(status?.name ?? "").label;
  return (
    <div className="ms-auto badge-cell" data-testid="phase-badge">
      <OverlayTrigger
        trigger="click"
        rootClose
        transition
        placement={/* istanbul ignore next */ isMobile ? "bottom" : "left"}
        onToggle={(nextShow: boolean) => {
          if (nextShow) {
            sendDataToGTM({
              event: "eventTracking",
              category: "Construction",
              action: `Tooltip Opened - ${badgeLabel} Badge`,
              label: "Project Details - Phase List",
            });
          }
        }}
        overlay={TOOL_TIP({
          packageStatus: status?.name ?? "",
        })}
      >
        <Badge className="badge-tooltip" bg={badgeColorFlag}>
          {badgeLabel}
        </Badge>
      </OverlayTrigger>
    </div>
  );
};

/* istanbul ignore next */
const renderIcon = (
  sortOrder: number,
  state: IdNamePairVM | undefined
): React.ReactNode => {
  return state?.id === "complete" ? (
    <div className="phase-icon-container complete">
      <FontAwesomeIcon
        className="phase-icon"
        icon={faCheckCircle}
        color={THEME.CE_THEME.colors.cta.primary}
        size="lg"
      />
    </div>
  ) : (
    <div className="phase-icon-container">
      <FontAwesomeIcon
        className="phase-icon"
        icon={faCircleNotch}
        color={THEME.CE_THEME.colors.grays.septenary}
        size="lg"
      />
      <span>{sortOrder + 1}</span>
    </div>
  );
};

/* istanbul ignore next */
const changeDate = (date: string | null | undefined | Date) => {
  if (date) {
    const D = new Date(date);
    const month = new Intl.DateTimeFormat("en-US", { month: "long" }).format(
      new Date(/*year:*/ 1, D.getUTCMonth())
    );
    const day = D.getUTCDate();

    return `${month + " " + day}`;
  }
};

const PHASE_LIST_ITEM: React.FC<IPhaseListItemProps> = (
  props: IPhaseListItemProps
) => {
  const sendDataToGTM = useGTMDispatch();
  const { phaseSummary } = props;
  const { phase, completionDate, status, sortOrder, state } = phaseSummary;

  /* istanbul ignore next */
  return (
    <PhaseListStyles.PhaseListItem
      className={`phase-list-item ${
        state?.id === EPhaseStates.PENDING ? EPhaseStates.PENDING : ""
      }`}
      direction="horizontal"
    >
      {renderIcon(sortOrder ?? -1, state)}
      {isMobile && (
        <Stack direction="vertical">
          <div
            className={`phase-name-cell ${
              state?.id === EPhaseStates.ACTIVE ? EPhaseStates.ACTIVE : ""
            }`}
          >
            {
              <OverlayTrigger
                trigger="click"
                rootClose
                transition
                placement="bottom"
                onToggle={(nextShow: boolean) => {
                  if (nextShow) {
                    sendDataToGTM({
                      event: "eventTracking",
                      category: "Construction",
                      action: `Tooltip Opened - ${phase?.name ?? ""}`,
                      label: "Project Details - Phase List",
                    });
                  }
                }}
                overlay={TOOL_TIP({
                  phaseName: phase?.name ?? "",
                })}
              >
                <span>{phase?.name ?? ""}</span>
              </OverlayTrigger>
            }
          </div>
          <div className="phase-date-cell">{changeDate(completionDate)}</div>
        </Stack>
      )}
      {!isMobile && (
        <>
          <div
            className={`phase-name-cell ${
              state?.id === EPhaseStates.ACTIVE ? EPhaseStates.ACTIVE : ""
            }`}
          >
            {
              <OverlayTrigger
                trigger="click"
                rootClose
                transition
                placement="right"
                onToggle={(nextShow: boolean) => {
                  if (nextShow) {
                    sendDataToGTM({
                      event: "eventTracking",
                      category: "Construction",
                      action: `Tooltip Opened - ${phase?.name ?? ""}`,
                      label: "Project Details - Phase List",
                    });
                  }
                }}
                overlay={TOOL_TIP({
                  phaseName: phase?.name ?? "",
                })}
              >
                <span>{phase?.name ?? ""}</span>
              </OverlayTrigger>
            }
          </div>
          <div className="phase-date-cell">{changeDate(completionDate)}</div>
        </>
      )}

      <div className="ms-auto badge-cell">
        {state &&
          state.id &&
          state.id.toLowerCase() === EPhaseStates.ACTIVE &&
          renderBadge(status, sendDataToGTM)}
      </div>
    </PhaseListStyles.PhaseListItem>
  );
};

export default PHASE_LIST_ITEM;
